/**
 * 시나리오별 "무엇을 보고 유량을 정할 것인가" 안내 패널.
 *
 * 계산기가 붙지 않는 14개 시나리오에서 설계자가 막히는 지점이 여기다.
 * 표준을 펴지 않아도 **확인할 자료와 순서**를 알 수 있어야 한다.
 */
import { useState } from "react";
import { findRateGuidance, COMMON_PRINCIPLES } from "./rateGuidance";
import type { SimpleFluidKind } from "./caseState";

/** **굵게** 표시만 처리한다. 안내문에서 강조가 자주 필요하다. */
function Marked({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? <b key={i}>{p}</b> : <span key={i}>{p}</span>,
      )}
    </>
  );
}

export function RateGuidancePanel({
  item,
  fluidKind,
}: {
  item: number;
  /** 액체 배출이면 액체 지침을, 그 밖에는 증기 지침을 앞세운다. */
  fluidKind: SimpleFluidKind;
}) {
  const [open, setOpen] = useState(false);
  const g = findRateGuidance(item);
  if (!g) return null;

  const isLiquid = fluidKind === "liquid";
  const primary = isLiquid ? g.liquid : g.vapor;
  const secondary = isLiquid ? g.vapor : g.liquid;
  const primaryLabel = isLiquid ? "액체 배출" : "증기 배출";
  const secondaryLabel = isLiquid ? "증기 배출" : "액체 배출";

  return (
    <div className="guidance">
      <div className="guidance__head">
        <strong>입력 자료 안내</strong>
        <span className="muted">API 521 {g.clause}</span>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? "닫기" : "열기"}
        </button>
      </div>

      <p className="guidance__lead">
        <span className="guidance__tag">{primaryLabel}</span>{" "}
        {primary === null ? (
          <span className="muted">표준이 이 상태의 지침을 두지 않습니다.</span>
        ) : (
          <Marked text={primary} />
        )}
      </p>

      {open && (
        <div className="guidance__body">
          {secondary !== null && (
            <p className="guidance__lead">
              <span className="guidance__tag">{secondaryLabel}</span>{" "}
              <Marked text={secondary} />
            </p>
          )}

          <h4>확인할 자료</h4>
          <ul>
            {g.documents.map((d) => (
              <li key={d}>
                <Marked text={d} />
              </li>
            ))}
          </ul>

          <h4>구하는 순서</h4>
          <ol>
            {g.steps.map((s) => (
              <li key={s}>
                <Marked text={s} />
              </li>
            ))}
          </ol>

          <h4>주의</h4>
          <ul className="guidance__cautions">
            {g.cautions.map((c) => (
              <li key={c}>
                <Marked text={c} />
              </li>
            ))}
          </ul>

          <h4>모든 시나리오 공통</h4>
          <ul>
            {COMMON_PRINCIPLES.map((c) => (
              <li key={c}>
                <Marked text={c} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
